import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import '@/styles/access.css';

const API = process.env.REACT_APP_BACKEND_URL;

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | valid | used | invalid
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const { data } = await axios.get(`${API}/api/invite/${token}`);
        if (data.valid) {
          setState('valid');
        } else {
          setState(data.reason === 'used' ? 'used' : 'invalid');
        }
      } catch {
        setState('invalid');
      }
    };
    validate();
  }, [token]);

  const handleEnter = async () => {
    setEntering(true);
    try {
      await axios.post(`${API}/api/invite/${token}/redeem`);
      localStorage.setItem('nymphe_access_token', token);
      localStorage.setItem('nymphe_entered', 'true');
      navigate('/app-preview');
    } catch {
      setState('used');
    }
    setEntering(false);
  };

  return (
    <div className="acc" data-testid="invite-page">
      <div className="acc-bg">
        <img src="/images/access-bg.png" alt="" />
        <div className="acc-bg-overlay" />
      </div>
      <div className="noise-overlay" />

      <AnimatePresence mode="wait">
        {state === 'loading' && (
          <motion.section key="load" className="acc-step" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
        )}

        {state === 'valid' && (
          <motion.section
            key="valid"
            className="acc-step"
            data-testid="invite-valid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="acc-content">
              <motion.h1
                className="acc-headline"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                You've been invited.
              </motion.h1>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                Your access is ready.
              </motion.p>
              <motion.button
                className="acc-btn"
                onClick={handleEnter}
                disabled={entering}
                data-testid="invite-enter-btn"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                {entering ? 'Opening...' : 'Enter NYMPHÉ'}
              </motion.button>
              <motion.p
                className="acc-success-micro"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.6 }}
                style={{ marginTop: 32 }}
              >
                This invitation is private.
              </motion.p>
            </div>
          </motion.section>
        )}

        {state === 'used' && (
          <motion.section
            key="used"
            className="acc-step"
            data-testid="invite-used"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="acc-content">
              <motion.h1
                className="acc-headline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                This invitation has already been used.
              </motion.h1>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                Request access again if needed.
              </motion.p>
              <motion.button
                className="acc-btn-return"
                onClick={() => navigate('/access')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                data-testid="invite-return-btn"
              >
                Return
              </motion.button>
            </div>
          </motion.section>
        )}

        {state === 'invalid' && (
          <motion.section
            key="invalid"
            className="acc-step"
            data-testid="invite-invalid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="acc-content">
              <motion.h1
                className="acc-headline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                This invitation is no longer available.
              </motion.h1>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                Request access to receive a new one.
              </motion.p>
              <motion.button
                className="acc-btn-return"
                onClick={() => navigate('/access')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                data-testid="invite-return-btn"
              >
                Return
              </motion.button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvitePage;
